import { useState, useEffect } from "react";
import {
  Layout,
  Text,
  Button,
  Modal,
  Padding,
  Icon,
  IconList,
  AnchorLink,
  Container,
} from "@kidslivesafe/sc-react";

import { useAppSelector, useAppDispatch } from "../../../hooks/hooks";
import {
  sendMemberEventAsync,
  addPdfUpsellAsync,
  createCustomerRedisTokenAsync,
} from "../../../features/admin/adminSlice";
import { selectBrandInfo } from "../../../features/brand/brandSlice";
import { selectCustomerId } from "../../../features/authentication/authenticationSlice";
import { formatPhoneNumber } from "../../../utils";

const PdfUpsellModal = ({
  showPdfUpsellModal,
  setShowPdfUpsellModal,
  onSuccessfulPurchase,
}: {
  showPdfUpsellModal: boolean;
  setShowPdfUpsellModal: (show: boolean) => void;
  onSuccessfulPurchase: () => void;
}) => {
  const dispatch = useAppDispatch();
  const brandInfo = useAppSelector(selectBrandInfo);
  const customerId = useAppSelector(selectCustomerId);
  const [pdfUpsellClicked, setPdfUpsellClicked] = useState<boolean>(false);
  const [pdfUpsellLoading, setPdfUpsellLoading] = useState<boolean>(false);
  const [failedPurchase, setFailedPurchase] = useState<boolean>(false);
  const [successfulPurchase, setSuccessfulPurchase] = useState<boolean>(false);

  const handlePdfUpsellPayment = async () => {
    setPdfUpsellClicked(true);
    setPdfUpsellLoading(true);
    try {
      dispatch(
        sendMemberEventAsync({
          EventCategory: "Button Click",
          EventAction: "Add On",
          MetaData: JSON.stringify({
            key: "PDF Upsell",
            value: "Purchase PDF",
          }),
        }),
      );
      if (customerId) {
        const savePdfUpsellAsyncResponse = await dispatch(
          addPdfUpsellAsync({ customerId }),
        );
        if (addPdfUpsellAsync.fulfilled.match(savePdfUpsellAsyncResponse)) {
          onSuccessfulPurchase();
          setSuccessfulPurchase(true);
        } else if (
          addPdfUpsellAsync.rejected.match(savePdfUpsellAsyncResponse)
        ) {
          setFailedPurchase(true);
        }
      }
    } catch (error) {
      console.error("PDF Upsell Payment Failed:", error);
    }
  };

  useEffect(() => {
    if (showPdfUpsellModal) {
      dispatch(
        sendMemberEventAsync({
          EventCategory: "Modal",
          EventAction: "Display",
          MetaData: JSON.stringify({
            key: "PDF Upsell",
            value: "Offer Display",
          }),
        }),
      );
    }
  }, [showPdfUpsellModal]);

  const handleExternalNavigation = async () => {
    const createCustomerRedisTokenAsyncResponse = await dispatch(
      createCustomerRedisTokenAsync(),
    );

    if (
      createCustomerRedisTokenAsync.fulfilled.match(
        createCustomerRedisTokenAsyncResponse,
      )
    ) {
      const uuid = createCustomerRedisTokenAsyncResponse.payload.token;
      window.location.href =
        brandInfo.homeUrl + "/update-credit-card?uuid=" + uuid + "&beta=true";
    }
  };

  return (
    <Modal
      open={showPdfUpsellModal}
      setOpen={() => {
        dispatch(
          sendMemberEventAsync({
            EventCategory: "Button Click",
            EventAction: "Exit Modal",
            MetaData: JSON.stringify({
              key: "PDF Upsell",
              value: "Abandon Offer",
            }),
          }),
        );
        setShowPdfUpsellModal(false);
      }}
      size="fit-content"
      xClose
      outSideClose
      scrollable={false}
    >
      <Padding space="sm" top="md" bottom="md">
        {pdfUpsellClicked && failedPurchase ? (
          <>
            <Layout
              type="flex"
              flexProps={{
                direction: "column",
                gap: "sm",
              }}
            >
              <div>
                <Text textAlign="center" tag="h2" fontWeight="semibold">
                  We've encountered a problem!{" "}
                </Text>
              </div>
              <Text textAlign="center" size="sm">
                We couldn’t process your payment, possibly due to an expired or
                declined card.
              </Text>
              <Text textAlign="center" size="sm">
                Please update your payment details to purchase PDF downloads.
              </Text>
            </Layout>
            <Padding top="md" bottom="md">
              <Button
                buttonText="Update Payment Method"
                handleClick={handleExternalNavigation}
                size="md"
                fill="fill"
              />
            </Padding>
            <Text textAlign="center" size="sm">
              For questions please contact member support at{" "}
              <AnchorLink
                inline
                phoneNumber
                hoverUnderline
                href={brandInfo.phoneNumber}
              >
                {formatPhoneNumber(brandInfo.phoneNumber)}
              </AnchorLink>
              .
            </Text>
          </>
        ) : pdfUpsellClicked && successfulPurchase ? (
          <>
            <Padding bottom="sm">
              <Layout
                type="flex"
                flexProps={{
                  direction: "column",
                  gap: "md",
                  alignItems: "center",
                }}
              >
                <Layout
                  type="flex"
                  flexProps={{
                    alignContent: "center",
                    justifyContent: "center",
                    direction: "row",
                    gap: "sm",
                  }}
                >
                  <Text
                    color="success"
                    textAlign="center"
                    tag="h2"
                    fontWeight="semibold"
                  >
                    <Icon size={24} name={IconList.IconCircleCheck} />
                  </Text>
                  <Text
                    color="success"
                    textAlign="center"
                    tag="h2"
                    fontWeight="semibold"
                  >
                    Success!{" "}
                  </Text>
                </Layout>
                <Layout
                  type="flex"
                  flexProps={{ direction: "column", gap: "lg" }}
                >
                  <Text textAlign="center" size="sm">
                    You have unlocked PDF access
                  </Text>
                </Layout>
                <Button
                  handleClick={() => {
                    setShowPdfUpsellModal(false);
                  }}
                  buttonText="Close"
                />
              </Layout>
            </Padding>
          </>
        ) : (
          <>
            <Layout
              type="flex"
              flexProps={{
                direction: "column",
                gap: "md",
              }}
            >
              <Layout
                type="flex"
                flexProps={{
                  direction: "column",
                  gap: "md",
                  alignItems: "center",
                }}
              >
                <Container altColor="alt" bodyBorderRadius>
                  <Padding space="xxs" left="xs" right="xs">
                    <Text textAlign="center" size="sm" fontWeight="semibold">
                      Premium Feature
                    </Text>
                  </Padding>
                </Container>
                <div>
                  <Text textAlign="center" tag="h2">
                    Unlock PDF Downloads
                  </Text>
                </div>
                <Text textAlign="center" size="sm" fontWeight="semibold">
                  Take your reports offline anytime with PDF downloads
                </Text>
                <Container altColor="alt" bodyBorderRadius>
                  <Padding space="sm" left="lg" right="lg">
                    <Layout
                      type="flex"
                      flexProps={{
                        gap: "xs",
                        alignItems: "flex-start",
                        direction: "column",
                      }}
                    >
                      <Layout
                        type="flex"
                        flexProps={{
                          gap: "xs",
                          alignItems: "center",
                        }}
                      >
                        <Icon name={IconList.IconCircle} size={6} />
                        <Text fontWeight="semibold" size="sm">
                          Download any report as a PDF
                        </Text>
                      </Layout>
                      <Layout
                        type="flex"
                        flexProps={{
                          gap: "xs",
                          alignItems: "center",
                        }}
                      >
                        <Icon name={IconList.IconCircle} size={6} />
                        <Text fontWeight="semibold" size="sm">
                          Access offline - they’re yours
                        </Text>
                      </Layout>
                      <Layout
                        type="flex"
                        flexProps={{
                          gap: "xs",
                          alignItems: "center",
                        }}
                      >
                        <Icon name={IconList.IconCircle} size={6} />
                        <Text fontWeight="semibold" size="sm">
                          Professional print-ready formatting
                        </Text>
                      </Layout>
                    </Layout>
                  </Padding>
                </Container>
                <Text textAlign="center" size="sm">
                  One-time $1.99 - no monthly fees!
                </Text>
              </Layout>
              <Layout
                type="flex"
                flexProps={{
                  direction: "column",
                  gap: "md",
                  alignItems: "center",
                }}
                fillHorizontal
              >
                <Button
                  buttonText="Yes, unlock PDFs for $1.99"
                  fill="fit-content"
                  handleClick={() => {
                    if (!pdfUpsellClicked) {
                      handlePdfUpsellPayment();
                    } else {
                      console.log("you can only purchase once");
                    }
                  }}
                  clicked={pdfUpsellClicked}
                  loading={pdfUpsellLoading}
                />

                <AnchorLink
                  handleClick={() => {
                    dispatch(
                      sendMemberEventAsync({
                        EventCategory: "Button Click",
                        EventAction: "Add On",
                        MetaData: JSON.stringify({
                          key: "PDF Upsell",
                          value: "Do Not Purchase CTA",
                        }),
                      }),
                    );
                    setShowPdfUpsellModal(false);
                  }}
                  size="xs"
                  role="button"
                  stayUnderline
                >
                  No, I Don't Need More Reports
                </AnchorLink>
              </Layout>
            </Layout>
          </>
        )}
      </Padding>
    </Modal>
  );
};

export default PdfUpsellModal;
